import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Loader2, CheckCircle2, Building2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [state, setState] = useState({ loading: true, invite: null, error: null });
  const [busy, setBusy] = useState(false);
  const [accepted, setAccepted] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get(`/school/invites/by-token/${token}`);
        if (active) setState({ loading: false, invite: data, error: null });
      } catch (err) {
        if (active) setState({ loading: false, invite: null, error: errMsg(err) });
      }
    })();
    return () => { active = false; };
  }, [token]);

  const accept = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/school/invites/accept/${token}`);
      setAccepted(data);
      toast.success(`You've joined ${data.school_name}`);
      setTimeout(() => navigate("/dashboard"), 1200);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (state.loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FBFAF4]">
        <Loader2 className="h-6 w-6 animate-spin text-[#0F7B7D]" />
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FBFAF4] px-4">
        <div className="max-w-md w-full bg-white border border-border rounded-[18px] p-7 text-center" data-testid="invite-error">
          <AlertCircle className="h-10 w-10 text-destructive mx-auto" />
          <h1 className="mt-4 text-xl font-display font-semibold">Invite not available</h1>
          <p className="mt-2 text-sm text-muted-foreground">{state.error}</p>
          <Button asChild className="mt-6 w-full h-11"><Link to="/">Back home</Link></Button>
        </div>
      </div>
    );
  }

  const invite = state.invite;
  const emailMismatch = user && user.email && invite?.email && user.email.toLowerCase() !== invite.email.toLowerCase();

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FBFAF4] px-4">
      <div className="max-w-md w-full bg-white border border-border rounded-[18px] p-7" data-testid="invite-card">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-[#D7F3F1] flex items-center justify-center"><Building2 className="h-6 w-6 text-[#0B6B6B]" /></div>
          <div>
            <div className="text-xs uppercase tracking-[0.14em] text-muted-foreground font-display">You're invited to join</div>
            <div className="text-xl font-display font-semibold leading-tight" data-testid="invite-school-name">{invite.school_name || "a school"}</div>
          </div>
        </div>
        <div className="mt-6 text-sm space-y-2">
          <div className="flex justify-between"><span className="text-muted-foreground">Sent to</span><span className="font-medium" data-testid="invite-email">{invite.email}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">School code</span><span className="font-mono font-semibold" data-testid="invite-code">{invite.school_code}</span></div>
        </div>

        {accepted ? (
          <div className="mt-6 rounded-xl bg-[#D7F3F1] p-4 text-sm text-[#123645] flex items-start gap-2" data-testid="invite-accepted">
            <CheckCircle2 className="h-4 w-4 mt-0.5 text-[#0F7B7D]" />
            You've joined <b>{accepted.school_name}</b>. Taking you to your dashboard…
          </div>
        ) : !user ? (
          <>
            <p className="mt-6 text-sm text-muted-foreground">Sign in or create a teacher account with this email to accept the invite.</p>
            <div className="mt-5 grid grid-cols-2 gap-3">
              <Button asChild variant="outline" className="h-11" data-testid="invite-signin"><Link to={`/login?next=/invite/${token}&email=${encodeURIComponent(invite.email)}`}>Sign in</Link></Button>
              <Button asChild className="h-11" data-testid="invite-signup"><Link to={`/signup?role=teacher&next=/invite/${token}&email=${encodeURIComponent(invite.email)}`}>Create account</Link></Button>
            </div>
          </>
        ) : user.role !== "teacher" ? (
          <div className="mt-6 rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900" data-testid="invite-wrong-role">
            Your current account is signed in as <b>{user.role}</b>. Only teacher accounts can accept this invite. Sign out and use a teacher account.
          </div>
        ) : emailMismatch ? (
          <div className="mt-6 rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900" data-testid="invite-email-mismatch">
            You're signed in as <b>{user.email}</b> but this invite was sent to <b>{invite.email}</b>. Sign out and use the invited email.
          </div>
        ) : (
          <Button onClick={accept} disabled={busy} className="mt-6 w-full h-11" data-testid="invite-accept-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Accept and join school
          </Button>
        )}
      </div>
    </div>
  );
}
